import type { MouseEvent, TouchEvent, ReactElement } from 'react';
import { Component, Fragment } from 'react';
import { localPoint } from '@visx/event';
import { max, min } from 'd3-array';
import type { PriceEntity } from '@components/PriceChart/utils/types';
import {
  xStock,
  yStock,
  getScaleY,
  bisectDate,
  getDataSlicedByPeriod,
  getDerivedStateUpdate,
} from './utils/stateUpdate';
import type { ScaleTime, ScaleLinear } from 'd3-scale';

interface PriceChartPanProps {
  now: number;
  data: PriceEntity[];
  dataInverse: PriceEntity[];
  width: number;
  height: number;
  period: number;
  children: (data: ChildrenProps) => ReactElement<ChildrenProps>;
  handleDragMove: () => void;
  handleScrollMove: () => void;
  handleDoubleClick: () => void;
}

interface ChildrenProps {
  dx: number;
  yMin?: number;
  yMax?: number;
  data: PriceEntity[];
  dataInverse: PriceEntity[];
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  transform: number;
  selectedX: number | null;
  selectedY: number | null;
  xScaleWidth: number;
  selectedDate?: Date;
  selectedClose?: number | null;
  isPositiveTrend: boolean;
  priceChange: number;
  firstPrice: number;
  lastPrice: number;
  handleDragEnd: () => void;
  handleDragMove: (e: MouseEvent | TouchEvent) => void;
  handleDragStart: (e: MouseEvent | TouchEvent) => void;
  handleScrollEnd: () => void;
  handleScrollMove: (n: number) => void;
  handleScrollStart: (n: number) => void;
  handleDoubleClick: (e: MouseEvent) => void;
}

interface PriceChartPanState {
  dx: number;
  yMin?: number;
  yMax?: number;
  yScale: ScaleLinear<number, number>;
  xScale: ScaleTime<number, number>;
  startX: number;
  isZooming: boolean;
  selectedX: number | null;
  selectedY: number | null;
  isDragging: boolean;
  isScrolling: boolean;
  xScaleWidth: number;
  selectedDate: Date;
  selectedClose: number | null;
  isPositiveTrend: boolean;
  priceChange: number;
  firstPrice: number;
  lastPrice: number;
}

class PriceChartPan extends Component<PriceChartPanProps, PriceChartPanState> {
  constructor(props) {
    super(props);
    const derivedState = getDerivedStateUpdate({
      dx: 0,
      now: props.now,
      data: props.data,
      width: props.width,
      reset: true,
      height: props.height,
      period: props.period,
    });
    this.state = {
      ...derivedState,
      isDragging: false,
      isScrolling: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { data, width, height, period, now } = this.props;
    const { dx, xScale, selectedDate, selectedClose, isZooming } = this.state;
    const { data: prevData, width: prevWidth, period: prevPeriod } = prevProps;

    let updateState = false;
    let reset = false;

    if (width !== prevWidth) {
      updateState = true;
    }

    if (period !== prevPeriod) {
      reset = isZooming === true ? false : true;
      updateState = true;
    }

    if (data !== undefined && prevData !== undefined) {
      if (data.length > 1 && prevData.length > 1) {
        if (
          data[0].close !== prevData[0].close ||
          data[data.length - 1].close !== prevData[prevData.length - 1].close
        ) {
          reset = true;
          updateState = true;
        }
      }
      if (data.length !== prevData.length && data.length > 0) {
        reset = true;
        updateState = true;
      }
    }

    if (updateState) {
      const nextState = getDerivedStateUpdate({
        dx,
        now,
        data,
        width,
        reset,
        height,
        xScale,
        period,
        selectedDate,
        selectedClose,
      });
      this.setState({
        ...nextState,
      });
    }
  }

  handleDoubleClick = (e) => {
    e.preventDefault();
    const { handleDoubleClick } = this.props;
    this.setState(
      {
        isZooming: true,
      },
      () => handleDoubleClick()
    );
  };

  handleDragStart = (e: MouseEvent | TouchEvent) => {
    try {
      const { dx, xScale, yScale } = this.state;
      const { data } = this.props;
      const point = localPoint(e);
      const x = point ? point.x : 0;
      const day = xScale.invert(-dx + x);
      if (!day) return false;
      const index = bisectDate(data, day);
      const selectedDate = xStock(data[index]);
      const selectedClose = yStock(data[index]);
      const selectedX = xScale(selectedDate) as number;
      const selectedY = selectedClose
        ? (yScale(selectedClose) as number)
        : selectedClose;
      this.setState({
        isDragging: true,
        dx: dx,
        startX: -dx + x,
        selectedX,
        selectedY,
        selectedDate,
        selectedClose,
      });
    } catch (error) {
      console.error(error);
    }
  };

  handleDragEnd = () => this.setState({ isDragging: false });

  handleDragMove = (e: MouseEvent | TouchEvent) => {
    const { handleHover } = this;
    const { data, width, height, handleDragMove } = this.props;
    const { dx, startX, xScale, isDragging, xScaleWidth, selectedClose } =
      this.state;

    if (!isDragging) {
      handleHover(e);
      return;
    }

    handleDragMove && handleDragMove();

    const point = localPoint(e);
    const nextX = point ? point.x : 0;

    let nextDx = dx;

    if (startX - nextX < 0) {
      nextDx = 0;
    } else if (startX - nextX > xScaleWidth - width) {
      nextDx = xScaleWidth - width;
    } else {
      nextDx = startX - nextX;
    }
    if (-nextDx === dx) return;
    //
    const nextData = getDataSlicedByPeriod(data, nextDx, xScale, width);
    //	Using the data slice, get the min and max prices from the Y axis
    const nextYMin = min(nextData, yStock) || 0;
    const nextYMax = max(nextData, yStock) || 0;

    //	Using the min and max, resync the yScale so it can show the volatity for the data slice
    const nextYScale = getScaleY(height, nextYMin || 0, nextYMax || 0);
    const nextSelectedY = selectedClose
      ? (nextYScale(selectedClose) as number)
      : selectedClose;
    //
    requestAnimationFrame(() => {
      this.setState({
        dx: -nextDx,
        yMin: nextYMin,
        yMax: nextYMax,
        yScale: nextYScale,
        selectedY: nextSelectedY,
      });
    });
  };

  handleHover = (e) => {
    try {
      const { dx, xScale, yScale } = this.state;
      const { data } = this.props;
      const point = localPoint(e);
      const x = point ? point.x : 0;
      const day = xScale.invert(-dx + x);
      if (!day) return false;
      const index = bisectDate(data, day);
      const selectedDate = xStock(data[index]);
      const selectedClose = yStock(data[index]);
      const selectedX = xScale(selectedDate) as number;
      const selectedY =
        selectedClose !== null
          ? (yScale(selectedClose) as number)
          : selectedClose;
      requestAnimationFrame(() => {
        this.setState({
          selectedX,
          selectedY,
          selectedDate,
          selectedClose,
        });
      });
    } catch (error) {
      console.error(error);
    }
  };

  handleScrollEnd = () => this.setState({ isScrolling: false });

  handleScrollMove = (dx) => {
    const { data, width, height, handleScrollMove } = this.props;
    handleScrollMove && handleScrollMove();
    const { xScale, selectedX, isScrolling, selectedDate, selectedClose } =
      this.state;
    if (!isScrolling) return;
    //
    //
    const nextData = getDataSlicedByPeriod(data, dx, xScale, width);
    //	Using the data slice, get the min and max prices from the Y axis
    const nextYMin = min(nextData, yStock) || 0;
    const nextYMax = max(nextData, yStock) || 0;
    //	Using the min and max, resync the yScale so it can show the volatity for the data slice
    const nextYScale = getScaleY(height, nextYMin || 0, nextYMax || 0);
    //
    const nextSelectedDate =
      selectedX &&
      (selectedX < dx || selectedX > dx + width) &&
      (nextYMin !== void 0 || nextYMax !== void 0)
        ? xStock(nextData[selectedX < dx ? 0 : nextData.length - 1])
        : selectedDate;
    const nextSelectedClose =
      selectedX &&
      (selectedX < dx || selectedX > dx + width) &&
      (nextYMin !== void 0 || nextYMax !== void 0)
        ? yStock(nextData[selectedX < dx ? 0 : nextData.length - 1])
        : selectedClose;
    //
    const nextSelectedX = xScale(nextSelectedDate) as number;
    const nextSelectedY = nextSelectedClose
      ? (nextYScale(nextSelectedClose) as number)
      : nextSelectedClose;

    const firstItem = nextData[0].close;
    const lastItem = nextData[nextData.length - 1].close;
    const isPositiveTrend = lastItem >= firstItem;

    requestAnimationFrame(() => {
      this.setState({
        dx: -dx,
        yMin: nextYMin,
        yMax: nextYMax,
        yScale: nextYScale,
        selectedX: nextSelectedX,
        selectedY: nextSelectedY,
        selectedDate: nextSelectedDate,
        selectedClose: nextSelectedClose,
        isPositiveTrend,
        priceChange: lastItem - firstItem,
        firstPrice: firstItem,
        lastPrice: lastItem,
      });
    });
  };

  handleScrollStart = (startXDelta) => {
    const { data, width, height } = this.props;
    const { dx, xScale, selectedX, xScaleWidth, selectedDate, selectedClose } =
      this.state;
    const adjustDelta = startXDelta < 0 || startXDelta > width;
    requestAnimationFrame(() => {
      if (adjustDelta) {
        let nextDx = -dx + startXDelta - width / 2;
        if (nextDx > xScaleWidth - width) {
          nextDx = xScaleWidth - width;
        } else if (nextDx < width / 2) {
          nextDx = 0;
        }
        //
        const nextData = getDataSlicedByPeriod(data, nextDx, xScale, width);
        //	Using the data slice, get the min and max prices from the Y axis
        const nextYMin = min(nextData, yStock) || 0;
        const nextYMax = max(nextData, yStock) || 0;
        //	Using the min and max, resync the yScale so it can show the volatity for the data slice
        const nextYScale = getScaleY(height, nextYMin || 0, nextYMax || 0);
        //
        const nextSelectedDate =
          selectedX && (selectedX < dx || selectedX > dx + width)
            ? xStock(selectedX < dx ? 0 : nextData[nextData.length - 1])
            : selectedDate;
        const nextSelectedClose =
          selectedX && (selectedX < dx || selectedX > dx + width)
            ? yStock(selectedX < dx ? 0 : nextData[nextData.length - 1])
            : selectedClose;
        //
        const nextSelectedX = xScale(nextSelectedDate) as number;
        const nextSelectedY = nextSelectedClose
          ? (nextYScale(nextSelectedClose) as number)
          : nextSelectedClose;
        //
        this.setState({
          dx: -nextDx,
          yMin: nextYMin,
          yMax: nextYMax,
          yScale: nextYScale,
          selectedX: nextSelectedX,
          selectedY: nextSelectedY,
          selectedDate: nextSelectedDate,
          selectedClose: nextSelectedClose,
          isScrolling: true,
        });
      } else {
        this.setState({
          isScrolling: true,
        });
      }
    });
  };

  render() {
    const {
      handleDragEnd,
      handleDragMove,
      handleDragStart,
      handleScrollEnd,
      handleScrollMove,
      handleScrollStart,
      handleDoubleClick,
    } = this;

    const {
      dx,
      yMin,
      yMax,
      yScale,
      xScale,
      selectedX,
      selectedY,
      xScaleWidth,
      selectedDate,
      selectedClose,
      isPositiveTrend,
      priceChange,
      firstPrice,
      lastPrice,
    } = this.state;

    const { data, dataInverse, width, children } = this.props;

    return (
      <Fragment>
        {children({
          dx,
          yMin,
          yMax,
          data,
          dataInverse,
          xScale,
          yScale,
          transform: dx + width - width,
          selectedX,
          selectedY,
          xScaleWidth,
          selectedDate,
          selectedClose,
          isPositiveTrend,
          priceChange,
          firstPrice,
          lastPrice,
          handleDragEnd,
          handleDragMove,
          handleDragStart,
          handleScrollEnd,
          handleScrollMove,
          handleScrollStart,
          handleDoubleClick,
        })}
      </Fragment>
    );
  }
}

export default PriceChartPan;
